import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  LinearScale,
  LineElement,
  PointElement,
  ScriptableLineSegmentContext,
  Tooltip,
  ChartOptions
} from 'chart.js'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import localeData from 'dayjs/plugin/localeData'
import classNames from 'classnames'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'next-i18next/pages'
import styles from '../Charts.module.scss'
import legendStyles from './ChartLegend.module.scss'
import { ChartLabels } from '../types'
import { PriceHistoryChartProps } from './types'
import { getPriceHistoryChartOptions } from './chartjs.config'
import useWindowSize from 'hooks/useWindowSize'
import annotationPlugin, { CoreAnnotationOptions } from 'chartjs-plugin-annotation'
import { getAlternatingBackground } from 'utils/chartjs'
import { externalTooltipHandler } from 'components/charts/chartjs.config'
import { formatAmount } from 'utils/format'

dayjs.extend(isSameOrAfter)
dayjs.extend(localeData)

ChartJS.register(CategoryScale, PointElement, LinearScale, Tooltip, LineElement, annotationPlugin)

const PriceHistoryChart = ({ activeProduct, data, months, minMaxData }: PriceHistoryChartProps) => {
  // Local constants
  const monthIdentifiers = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

  // Local state
  const [columnWidth, setColumnWidth] = useState<number>(0)
  const [chartYAxisWidth, setChartYAxisWidth] = useState<number>(0)
  const [chartXAxisHeight, setChartXAxisHeight] = useState<number>(0)
  const [chartWidth, setChartWidth] = useState<number>(0)

  // i18n
  const { t } = useTranslation('pricelists')

  // Window size
  const { isTablet } = useWindowSize()

  // Reference
  const chartRef = useRef<any>(null)
  const monthIndicatorRef = useRef<HTMLDivElement>(null)

  /**
   * sets the local state sizes for custom styling
   * @param {ChartJS} chart
   * @param {ChartLabels} labels
   */
  const handleResizeChart = useCallback((chart: ChartJS, labels: ChartLabels) => {
    if (chart) {
      const xLayoutItem = chart.boxes[1]
      const yLayoutItem = chart.boxes[0]

      setColumnWidth(xLayoutItem.width / labels.length)
      setChartYAxisWidth(xLayoutItem.left)
      setChartXAxisHeight(chart.height - yLayoutItem.bottom)
      setChartWidth(xLayoutItem.width)
    }
  }, [])

  // Table data
  const chartData = useMemo<ChartData<'line', (number | null)[], unknown>>(
    () => ({
      labels: months.map((month) => monthIdentifiers[dayjs(month).month()]),
      datasets: [
        {
          data: data.map((dataPoint) => dataPoint.price),
          fill: false,

          segment: {
            borderDash: (ctx: ScriptableLineSegmentContext) => {
              // TODO Look at what transfo returns and adapt the calculation accordingly
              const date = dayjs(data?.[ctx.p0DataIndex].date)
              const currentFormat = dayjs().format('MM-YYYY')
              const dateFormat = dayjs(date)?.format('MM-YYYY')
              const sameMonthOrLater = currentFormat === dateFormat || dayjs(date).isSameOrAfter(dayjs())

              return sameMonthOrLater ? [6, 6] : undefined
            }
          },
          tension: 0,
          pointRadius: 3,
          pointBackgroundColor: 'black'
        }
      ]
    }),
    [activeProduct, data, months]
  )

  // Memoized constants
  const alternatingBackground = useMemo<CoreAnnotationOptions>(
    () => getAlternatingBackground(months, window.getComputedStyle(document.documentElement).getPropertyValue('--bg-layer')),
    [months]
  )

  const options = useMemo<ChartOptions>(() => {
    const baseOptions = getPriceHistoryChartOptions(months, handleResizeChart, minMaxData, alternatingBackground, isTablet)
    return {
      ...baseOptions,
      elements: {
        line: {
          borderColor: window.getComputedStyle(document.documentElement).getPropertyValue('--border-high-contrast')
        },
        point: {
          backgroundColor: window.getComputedStyle(document.documentElement).getPropertyValue('--border-high-contrast')
        }
      },
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          // Disable the on-canvas tooltip
          enabled: false,
          external: (context: any) =>
            externalTooltipHandler({
              context,
              totalColumns: months.length,
              hideTitle: true
            }),
          bodyAlign: 'center',
          titleAlign: 'center',
          callbacks: {
            label: (context: any) =>
              `${dayjs.months()[context.dataIndex + (12 - context.dataset.data.length)]}: € ${formatAmount(context.raw, 3)}`,
            title: (context: any) => context?.[0]?.dataset?.label
          }
        }
      },
      scales: {
        y: {
          title: {
            display: true,
            text: '€/kWh'
          },
          ticks: {
            callback: (value) => `€ ${value}`
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      }
    } as ChartOptions
  }, [activeProduct, handleResizeChart, isTablet, months])

  // Set the state dimensions
  useEffect(() => {
    if (chartRef?.current || !isTablet) {
      handleResizeChart(chartRef.current, months)
    }
  }, [chartRef, handleResizeChart, isTablet, months])

  // Set the styling of the indicators
  useEffect(() => {
    if (columnWidth && chartYAxisWidth && chartXAxisHeight && chartWidth && monthIndicatorRef?.current) {
      monthIndicatorRef.current.style.left = chartYAxisWidth - columnWidth / 2 - 1 + 'px'
      monthIndicatorRef.current.style.height = `calc(100% - ${chartXAxisHeight - 7}px)`
      monthIndicatorRef.current.style.opacity = '1'
    }
  }, [chartWidth, chartXAxisHeight, chartYAxisWidth, columnWidth])

  return (
    <>
      <div className={styles['chart-wrapper']}>
        <Line options={options as ChartOptions<'line'>} data={chartData} className={styles.chart} ref={chartRef} />
      </div>

      {/* LEGEND */}
      <ul className={legendStyles.legend}>
        <li className={legendStyles['legend-item']}>
          <div className={legendStyles['line-indicator']} />
          <span>{t(`chart.labels.${activeProduct}.knownPrice`)}</span>
        </li>
        <li className={legendStyles['legend-item']}>
          <div className={classNames(legendStyles['line-indicator'], legendStyles.dashed)} />
          <span>{t(`chart.labels.${activeProduct}.unknownPrice`)}</span>
        </li>
      </ul>
    </>
  )
}

export default PriceHistoryChart
